import { useEffect, useState } from "react";
import { Link2, Loader2, Copy, Trash2, FolderOpen, X, Plus, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SharePointPickerDialog } from "@/components/SharePointPickerDialog";
import { QRDialog } from "@/components/QRDialog";
import { useAccount } from "@/context/AccountContext";

export function DropLinkManager({ open, onClose }) {
  const { currentKey, current } = useAccount() || {};
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [expires, setExpires] = useState(7);
  const [folder, setFolder] = useState(null);
  const [folderOpen, setFolderOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qrFor, setQrFor] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/droplinks", { params: { account: currentKey } }).then(({ data }) => setLinks(data)).catch(() => {}).finally(() => setLoading(false));
  }, [open, currentKey]);

  if (!open) return null;

  const create = async () => {
    if (!name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
    if (!folder) { toast.error("Bitte einen SharePoint-Zielordner wählen"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/droplinks", {
        name: name.trim(), drive_id: folder.drive_id, folder_id: folder.folder_id,
        folder_name: folder.name, expires_days: expires, account_key: currentKey,
      });
      setLinks((prev) => [data, ...prev]);
      setName(""); setFolder(null);
      copy(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link konnte nicht erstellt werden");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (id) => {
    const url = `${window.location.origin}/drop/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert ✅ — einfach an den externen Creator senden");
    } catch {
      toast.info(`Link: ${url}`, { duration: 10000 });
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Diesen Drop-Link deaktivieren?")) return;
    try {
      await api.delete(`/droplinks/${id}`);
      setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, active: false } : l)));
    } catch { toast.error("Deaktivieren fehlgeschlagen"); }
  };

  const isExpired = (l) => l.expires_at && l.expires_at < new Date().toISOString();

  return (
    <div className="fixed inset-0 z-[10007] flex items-center justify-center p-4" data-testid="droplink-manager">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <p className="text-sm font-bold text-neutral-900 flex items-center gap-2">
            <Link2 className="h-4 w-4 text-violet-600" /> Externe Upload-Links
          </p>
          <button onClick={onClose} data-testid="droplink-close" className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-400"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-3 border-b border-neutral-100">
          <p className="text-xs font-semibold text-violet-700" data-testid="droplink-channel">
            Zielkanal: {current?.name || currentKey}
          </p>
          <input data-testid="droplink-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Name, z. B. „Fotos Stadtfest 2026“ *"
            className="w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm" />
          <div className="flex gap-2">
            <button data-testid="droplink-pick-folder" onClick={() => setFolderOpen(true)}
              className={`flex-1 h-10 px-3 rounded-lg border text-sm font-semibold flex items-center gap-2 justify-center ${folder ? "border-violet-300 bg-violet-50 text-violet-700" : "border-neutral-200 text-neutral-500"}`}>
              <FolderOpen className="h-4 w-4" /> {folder ? folder.name : "SharePoint-Ordner wählen *"}
            </button>
            <select data-testid="droplink-expiry" value={expires} onChange={(e) => setExpires(Number(e.target.value))}
              className="h-10 px-2 rounded-lg border border-neutral-200 text-sm bg-white">
              <option value={7}>7 Tage gültig</option>
              <option value={30}>30 Tage gültig</option>
              <option value={0}>Unbegrenzt</option>
            </select>
          </div>
          <button data-testid="droplink-create" onClick={create} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Link erstellen & kopieren
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="py-8 flex justify-center text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : links.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">Noch keine Links erstellt.</p>
          ) : (
            links.map((l) => (
              <div key={l.id} data-testid={`droplink-row-${l.id}`} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ${l.active && !isExpired(l) ? "" : "opacity-50"}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-800 truncate">{l.name}</p>
                  <p className="text-[11px] text-neutral-400 truncate">
                    → {l.folder_name || "Dokumente"} · {l.upload_count || 0} Upload{(l.upload_count || 0) === 1 ? "" : "s"}
                    {!l.active ? " · deaktiviert" : isExpired(l) ? " · abgelaufen" : l.expires_at ? ` · bis ${l.expires_at.slice(0, 10)}` : ""}
                  </p>
                </div>
                {l.active && !isExpired(l) && (
                  <>
                    <button data-testid={`droplink-copy-${l.id}`} onClick={() => copy(l.id)} title="Link kopieren"
                      className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-violet-600">
                      <Copy className="h-4 w-4" />
                    </button>
                    <button data-testid={`droplink-qr-${l.id}`} onClick={() => setQrFor(l)} title="QR-Code anzeigen"
                      className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-violet-600">
                      <QrCode className="h-4 w-4" />
                    </button>
                  </>
                )}
                {l.active && (
                  <button data-testid={`droplink-deactivate-${l.id}`} onClick={() => deactivate(l.id)} title="Deaktivieren"
                    className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-neutral-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        <SharePointPickerDialog
          open={folderOpen}
          onClose={() => setFolderOpen(false)}
          pickFolder
          onPickedFolder={(f) => { setFolder(f); setFolderOpen(false); }}
        />
        <QRDialog open={!!qrFor} onClose={() => setQrFor(null)} url={qrFor ? `${window.location.origin}/drop/${qrFor.id}` : ""} title={qrFor?.name || "Drop-Link"} />
      </div>
    </div>
  );
}
